import {Fragment, useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Divider, SessionTextChat} from 'components';
import {ACTIVITY_NAME_TRANSLATE} from 'constants/session';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {SessionActivityName, SessionActivityTextChatData} from 'interfaces';
import {TranslationSettings as TranslationSettingsType} from 'types';

import {TranslationSettings} from '../../TranslationSettings';
import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

export const TextChatSidebar: React.FC = () => {
    const {t} = useTranslation();
    const {
        session, textChatThreads, requestAiSuggestions, aiSuggestionsLoading, aiSuggestions, aiSuggestionsError,
        onTextChatFileDownload, onTextChatSubmitMessage, onTextChatChangeState, onTextChatNewMessage, onActivityParticipantLanguageChange,
    } = useContext(UserSessionContext);

    const {textChatActivity, canManageSession, isTextChatTranslationEnabled, aiSuggestionsEnabled} = useUserSession();

    const translationSettings = useMemo(() =>
        (textChatActivity?.data as SessionActivityTextChatData)?.translationSettings, [textChatActivity]);

    const onChangeLanguage = useCallback((data: TranslationSettingsType) => {
        // use unified updateTranscriptionSettings from useContext
        const participantId = Object.keys(data)[0];
        onActivityParticipantLanguageChange?.(textChatActivity, participantId, data[participantId].language);
    }, [onActivityParticipantLanguageChange, textChatActivity]);

    return (
        <SidebarContent
            view={SidebarView.TextChat}
            header={
                <div className='tw-ts-h5'>
                    {t(ACTIVITY_NAME_TRANSLATE[SessionActivityName.TextChat])}
                </div>
            }
        >
            <div className='tw-flex tw-flex-col tw-gap-2 tw-h-full'>
                {isTextChatTranslationEnabled &&
                    <Fragment>
                        <TranslationSettings
                            session={session}
                            disabled={!canManageSession}
                            settings={translationSettings}
                            onChange={onChangeLanguage}
                        />
                        <Divider />
                    </Fragment>}
                <div className='tw-flex-1'>
                    <SessionTextChat
                        session={session}
                        aiSuggestionsEnabled={aiSuggestionsEnabled}
                        aiSuggestionsLoading={aiSuggestionsLoading}
                        aiSuggestionsError={aiSuggestionsError}
                        aiSuggestions={aiSuggestions}
                        requestAiSuggestions={requestAiSuggestions}
                        threads={textChatThreads}
                        onFileDownload={onTextChatFileDownload}
                        onSubmitMessage={onTextChatSubmitMessage}
                        onChangeState={onTextChatChangeState}
                        onNewMessage={onTextChatNewMessage}
                    />
                </div>
            </div>
        </SidebarContent>
    );
};
